'use client';

import React, { useState, useEffect } from 'react';
import { MapPin, BedDouble, Bath, Maximize2, Calendar, Car, Zap, Droplets, Building, Layers, Compass, CheckCircle, Phone, ChevronLeft, ChevronRight, IndianRupee, Shield, Wifi, Lock, MessageCircle, Mail, Flag, X, Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import AppImage from '@/components/ui/AppImage';
import { Property } from '@/lib/mockProperties';
import { reportService } from '@/lib/services/dataService';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface PropertyDetailModalProps {
  property: Property;
  onClose: () => void;
  onContact: () => void;
}

interface UnlockedContactDetails {
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  address: string;
  googleMapsLink: string;
  landmarks: string;
}

export default function PropertyDetailModal({ property, onClose, onContact }: PropertyDetailModalProps) {
  const [activePhoto, setActivePhoto] = useState(0);
  const [unlockState, setUnlockState] = useState<'locked' | 'ad' | 'unlocked'>('locked');
  const [adCountdown, setAdCountdown] = useState(5);
  const [unlockedDetails, setUnlockedDetails] = useState<UnlockedContactDetails | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const { user } = useAuth();

  // Increment view count when modal opens
  useEffect(() => {
    const incrementViews = async () => {
      try {
        const supabase = createClient();
        await supabase.rpc('increment_listing_views', { listing_id: property.id }).maybeSingle();
      } catch {
        // non-critical
      }
    };
    incrementViews();
  }, [property.id]);

  // Fetch sensitive contact details from server after unlock
  const fetchContactDetails = async () => {
    setFetchingDetails(true);
    try {
      const res = await fetch(`/api/property/contact-details?listing_id=${property.id}`);
      if (res.ok) {
        const data: UnlockedContactDetails = await res.json();
        setUnlockedDetails(data);
      }
    } catch {
      // silent
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleUnlock = () => {
    setUnlockState('ad');
    setAdCountdown(5);
    const interval = setInterval(() => {
      setAdCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setUnlockState('unlocked');
          fetchContactDetails();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const whatsappMsg = unlockedDetails
    ? encodeURIComponent(`Hi, I'm interested in your property: ${property.title} listed on tolettu.com. Is it still available?`)
    : '';

  const submitReport = async () => {
    if (!reportReason.trim()) return;
    setReportSubmitting(true);
    try {
      await reportService.createReport({
        listing_id: property.id,
        reporter_id: user?.id || null,
        reporter_name: user?.user_metadata?.full_name || user?.email || 'Anonymous',
        reason: reportReason,
        status: 'Open',
      });
    } catch {
      // silent — still show success to user
    } finally {
      setReportSubmitting(false);
      setReportSent(true);
      setTimeout(() => { setReportOpen(false); setReportSent(false); setReportReason(''); }, 1500);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={property.title} size="xl">
      <div className="p-0">
        {/* Photo Gallery */}
        <div className="relative h-72 sm:h-96 bg-muted overflow-hidden">
          <AppImage
            src={property.photos[activePhoto]}
            alt={`${property.title} interior view ${activePhoto + 1}`}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 896px"
          />
          {property.photos.length > 1 && (
            <>
              <button
                onClick={() => setActivePhoto((p) => (p - 1 + property.photos.length) % property.photos.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors z-10"
                aria-label="Previous photo"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setActivePhoto((p) => (p + 1) % property.photos.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors z-10"
                aria-label="Next photo"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {property.photos.map((_, i) => (
              <button
                key={`detail-dot-${property.id}-${i}`}
                onClick={() => setActivePhoto(i)}
                className={`rounded-full transition-all ${
                  i === activePhoto ? 'bg-white w-5 h-2' : 'bg-white/60 w-2 h-2'
                }`}
                aria-label={`View photo ${i + 1}`}
              />
            ))}
          </div>
          {/* Thumbnail Strip */}
          <div className="absolute bottom-0 left-0 right-0 flex gap-1.5 px-3 pb-3 overflow-x-auto scrollbar-thin z-10" style={{ paddingTop: '2.5rem' }}>
            {property.photos.slice(0, 6).map((photo, i) => (
              <button
                key={`thumb-${property.id}-${i}`}
                onClick={() => setActivePhoto(i)}
                className={`shrink-0 w-14 h-10 rounded-lg overflow-hidden border-2 transition-all ${
                  i === activePhoto ? 'border-white' : 'border-transparent opacity-70 hover:opacity-100'
                }`}
                aria-label={`View photo ${i + 1}`}
              >
                <AppImage
                  src={photo}
                  alt={`${property.title} thumbnail ${i + 1}`}
                  width={56}
                  height={40}
                  className="object-cover w-full h-full"
                />
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap gap-2 mb-2">
                {property.verified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                    <CheckCircle size={10} />
                    Verified
                  </span>
                )}
                {property.featured && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                    Featured
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-foreground">{property.title}</h2>
              {/* Address: only shown after unlock */}
              <div className="flex items-center gap-1 text-muted-foreground mt-1">
                <MapPin size={14} />
                <span className="text-sm">
                  {unlockState === 'unlocked' && unlockedDetails?.address
                    ? unlockedDetails.address
                    : `${property.locality}, ${property.city}`}
                </span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-3xl font-extrabold text-primary tabular-nums">
                ₹{property.monthlyRent.toLocaleString('en-IN')}
              </div>
              <div className="text-sm text-muted-foreground">/month</div>
              {property.negotiable && (
                <div className="text-xs text-green-600 font-semibold mt-1">Negotiable</div>
              )}
            </div>
          </div>

          {/* Quick Specs */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[
              { icon: BedDouble, label: 'BHK', value: property.bhk },
              { icon: Bath, label: 'Bathrooms', value: `${property.bathrooms}` },
              { icon: Maximize2, label: 'Area', value: `${property.builtUpArea} sq.ft` },
              { icon: Layers, label: 'Floor', value: `${property.floorNumber}${property.floorNumber === 1 ? 'st' : property.floorNumber === 2 ? 'nd' : 'th'}` },
              { icon: Compass, label: 'Facing', value: property.facing },
              { icon: Calendar, label: 'Available', value: property.availableFrom },
            ].map((spec) => (
              <div
                key={`spec-${spec.label}`}
                className="flex flex-col items-center gap-1.5 p-3 bg-muted rounded-xl text-center"
              >
                <spec.icon size={16} className="text-primary" />
                <div className="text-xs text-muted-foreground">{spec.label}</div>
                <div className="text-xs font-bold text-foreground">{spec.value}</div>
              </div>
            ))}
          </div>

          {/* Rent Details */}
          <div className="section-card">
            <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
              <IndianRupee size={16} className="text-primary" />
              Rent Details
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">Monthly Rent</div>
                <div className="text-base font-bold text-foreground tabular-nums">
                  ₹{property.monthlyRent.toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Security Deposit</div>
                <div className="text-base font-bold text-foreground tabular-nums">
                  ₹{property.securityDeposit.toLocaleString('en-IN')}
                </div>
              </div>
              {property.maintenanceCharges > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Maintenance</div>
                  <div className="text-base font-bold text-foreground tabular-nums">
                    ₹{property.maintenanceCharges.toLocaleString('en-IN')}/mo
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Amenities */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-3">Amenities & Features</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { show: property.parking, icon: Car, label: 'Parking' },
                { show: property.powerBackup, icon: Zap, label: 'Power Backup' },
                { show: property.waterFacility, icon: Droplets, label: '24hr Water' },
                { show: property.lift, icon: Building, label: 'Lift' },
                { show: property.wifi, icon: Wifi, label: 'WiFi Ready' },
              ]
                .filter((a) => a.show)
                .map((amenity) => (
                  <span
                    key={`amenity-${amenity.label}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-secondary text-secondary-foreground text-xs font-semibold"
                  >
                    <amenity.icon size={12} />
                    {amenity.label}
                  </span>
                ))}
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted text-muted-foreground text-xs font-semibold">
                {property.furnishing}
              </span>
            </div>
          </div>

          {/* Map Embed — only shown after unlock */}
          {unlockState === 'unlocked' && unlockedDetails?.googleMapsLink && (
            <div>
              <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                <MapPin size={16} className="text-primary" />
                Location
              </h3>
              <div className="rounded-xl overflow-hidden border border-border h-52 bg-muted flex items-center justify-center">
                <div className="text-center">
                  <MapPin size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{unlockedDetails.address}</p>
                  <a
                    href={unlockedDetails.googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary font-semibold hover:underline mt-1 block"
                  >
                    Open in Google Maps →
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Nearby Landmarks — only shown after unlock */}
          {unlockState === 'unlocked' && unlockedDetails?.landmarks && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-2">Nearby Landmarks</div>
              <p className="text-sm text-foreground">{unlockedDetails.landmarks}</p>
            </div>
          )}

          {/* Contact CTA */}
          <div className="pt-2 border-t border-border space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield size={16} className="text-primary" />
              </div>
              <div>
                <div className="text-xs font-bold text-foreground">{property.ownerName}</div>
                <div className="text-xs text-muted-foreground">Verified Owner</div>
              </div>
            </div>

            {unlockState === 'locked' && (
              <div className="space-y-2">
                <button onClick={handleUnlock} className="btn-primary w-full justify-center gap-2">
                  <Lock size={15} />🔓 Unlock Property Details
                </button>
                <button onClick={() => setReportOpen(true)}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold text-muted-foreground hover:text-red-500 hover:bg-red-50 border border-border hover:border-red-200 transition-all">
                  <Flag size={12} />Report Listing
                </button>
              </div>
            )}

            {unlockState === 'ad' && (
              <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-4 text-center">
                <div className="text-xs font-bold text-amber-700 mb-1.5">📢 Advertisement</div>
                <div className="text-sm font-semibold text-foreground mb-1">QuickMove Packers & Movers</div>
                <div className="text-xs text-muted-foreground mb-3">Trusted relocation services across India. Get 20% off on first booking!</div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                  <span className="w-5 h-5 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-extrabold">{adCountdown}</span>
                  Property details unlock in {adCountdown}s
                </div>
              </div>
            )}

            {unlockState === 'unlocked' && (
              <div className="space-y-2">
                {fetchingDetails ? (
                  <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" />
                    Loading contact details...
                  </div>
                ) : unlockedDetails ? (
                  <>
                    <div className="text-xs text-center text-green-600 font-semibold flex items-center justify-center gap-1">
                      <CheckCircle size={12} />✓ Property Details Unlocked
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <a href={`tel:${unlockedDetails.ownerPhone}`}
                        className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity">
                        <Phone size={15} />Call
                      </a>
                      <a href={`https://wa.me/91${unlockedDetails.ownerPhone.replace(/\D/g, '')}?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer"
                        className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors">
                        <MessageCircle size={15} />WhatsApp
                      </a>
                      <a href={`mailto:${unlockedDetails.ownerEmail}?subject=Inquiry about ${property.title}&body=Hi, I'm interested in your property listed on tolettu.com.`}
                        className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-muted text-foreground text-xs font-semibold hover:bg-border transition-colors">
                        <Mail size={15} />Email
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-center text-muted-foreground py-2">
                    Could not load contact details. Please try again.
                  </div>
                )}
                <button onClick={() => setReportOpen(true)}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold text-muted-foreground hover:text-red-500 hover:bg-red-50 border border-border hover:border-red-200 transition-all">
                  <Flag size={12} />Report Listing
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {reportOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setReportOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-card rounded-2xl shadow-modal w-full max-w-sm p-6 fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center"><Flag size={16} className="text-red-600" /></div>
                <h3 className="text-base font-extrabold text-foreground">Report Listing</h3>
              </div>
              <button onClick={() => setReportOpen(false)} className="p-1.5 rounded-lg hover:bg-muted" aria-label="Close"><X size={18} /></button>
            </div>
            {reportSent ? (
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm font-semibold">
                <CheckCircle size={16} />Report submitted. Thank you!
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2 mb-4">
                  {['Fake or misleading listing', 'Wrong location or address', 'Inappropriate content', 'Already rented out', 'Spam or duplicate', 'Other'].map((reason) => (
                    <label key={reason} className="flex items-center gap-2.5 cursor-pointer group">
                      <input type="radio" name={`detail-report-${property.id}`} value={reason} checked={reportReason === reason} onChange={() => setReportReason(reason)} className="accent-primary w-4 h-4" />
                      <span className="text-sm text-foreground group-hover:text-primary transition-colors">{reason}</span>
                    </label>
                  ))}
                </div>
                <button onClick={submitReport} disabled={!reportReason || reportSubmitting}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <Flag size={14} />{reportSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}